import { useReducer, useCallback } from 'react';
import { TreeNodeState } from './types';

/**
 * We keep a Set of expanded node IDs in state.
 */
export type TreeState = Map<string, TreeNodeState>;

/**
 * Actions that our reducer can handle.
 */
export enum TreeActionType {
  ToggleCollapse = 'TOGGLE_COLLAPSE',
  Refresh = 'REFRESH',
  Select = 'SELECT',
}

interface ToggleCollapseAction {
  type: TreeActionType.ToggleCollapse;
  id: string;
}

interface SelectAction {
  type: TreeActionType.Select;
  id: string;
}

interface RefreshAction {
  type: TreeActionType.Refresh;
}

export type TreeAction = ToggleCollapseAction | SelectAction | RefreshAction;

/**
 * Reducer that takes the current Set of expanded IDs and one action,
 * and returns a new Set accordingly.
 */
export function treeReducer(state: TreeState, action: TreeAction): TreeState {
  switch (action.type) {
    case TreeActionType.ToggleCollapse: {
      const next = new Map(state);
      const current = next.get(action.id) ?? { collapsed: true, selected: false };
      next.set(action.id, { ...current, collapsed: !current.collapsed });
      return next;
    }
    case TreeActionType.Select: {
      const next = new Map(state);
      const current = next.get(action.id) ?? { collapsed: true, selected: false };
      // Deselect all other nodes
      for (const [key, value] of next.entries()) {
        next.set(key, { ...value, selected: false });
      }
      // Select the current node
      next.set(action.id, { ...current, selected: !current.selected });
      return next;
    }
    case TreeActionType.Refresh: {
      // This action could be used to reset or refresh the state if needed
      return new Map(state); // No change, just return current state
    }
    default:
      return state;
  }
}

/**
 * Hook that wraps the reducer above. You can optionally pass
 * an initial array of IDs that should start “expanded.”
 */
export function useTreeReducer() {
  const [treeState, dispatch] = useReducer(treeReducer, new Map());

  // Action creators
  const toggleNode = useCallback(
    (id: string) => dispatch({ type: TreeActionType.ToggleCollapse, id }),
    [],
  );
  const refreshTree = useCallback(() => dispatch({ type: TreeActionType.Refresh }), []);

  const selectNode = useCallback((id: string) => dispatch({ type: TreeActionType.Select, id }), []);

  return {
    state: treeState,
    toggleNode,
    refreshTree,
    selectNode,
  };
}
